import { createContext, useReducer } from "react";

import { useMutation } from "@apollo/client";
import PropTypes from "prop-types";

import { useReferralPartnerId } from "components/hooks/partners/useReferralPartnerId";
import { useErrorNotifier } from "components/instrumentation/source";
import { useAnalyticsTracker } from "components/instrumentation/source/analytics";
import { WWW_INQUIRY_MUTATION } from "components/pages/property/listing/graphql";
import { useAppContext, useBootstrap } from "components/store/source";
import { buildingListingTrackingEvents } from "components/store/source/tracking_events";

const initialInquiryState = {
  hasError: false,
  inquiry: null,
  loading: false,
};

function inquiryReducer(state = initialInquiryState, action) {
  switch (action.type) {
    case "CREATE_INQUIRY_START":
      return { ...state, hasError: false, loading: true };
    case "CREATE_INQUIRY_SUCCESS":
      return {
        ...state,
        hasError: false,
        loading: action.isLoading,
        inquiry: action.value,
      };
    case "CREATE_INQUIRY_FAILURE":
      // TODO: parse clean error messages from GraphQL mutation
      return { ...state, hasError: true, loading: false, inquiry: null };
    default:
      return state;
  }
}

export const InquiryContext = createContext(initialInquiryState);

export function InquiryContextProvider({ children }) {
  const { currency } = useAppContext();
  const { mahalo } = useBootstrap();
  const { analytics } = useAnalyticsTracker();
  const [state, dispatch] = useReducer(inquiryReducer, initialInquiryState);

  const [createInquiryMutation] = useMutation(WWW_INQUIRY_MUTATION);
  const { referralPartnerId } = useReferralPartnerId();

  const errorNotifier = useErrorNotifier();

  async function createInquiry({
    listingId,
    collectionId,
    checkInDate,
    checkOutDate,
    promoCodes,
    buildingListing = false,
    guestNb,
  }) {
    try {
      dispatch({ type: "CREATE_INQUIRY_START" });

      const { data } = await createInquiryMutation({
        variables: {
          listingId,
          collectionId,
          checkInDate,
          checkOutDate,
          promoCodes,
          guestNb,
          ...(referralPartnerId && { referralPartnerId }),
        },
      });
      const isInquirySuccess =
        data && data.createInquiry && data.createInquiry.payPath;

      const trackingInfo =
        (data && data.createInquiry && data.createInquiry.trackingInfo) || {};

      await analytics.track(buildingListingTrackingEvents.bookingInquired, {
        ...trackingInfo,
        buildingListing,
        selectedCurrency: currency,
      });

      if (isInquirySuccess) {
        dispatch({
          type: "CREATE_INQUIRY_SUCCESS",
          value: data.createInquiry,
          // Do not turn off loading so user cannot click book again in web
          // In app, clear loading state, as we stack a new screen above.
          isLoading: !mahalo,
        });
      } else {
        dispatch({ type: "CREATE_INQUIRY_FAILURE" });
      }
    } catch (e) {
      errorNotifier.notify(e);
      dispatch({ type: "CREATE_INQUIRY_FAILURE" });
    }
  }

  return (
    <InquiryContext.Provider
      value={{
        ...state,
        createInquiry,
      }}
    >
      {children}
    </InquiryContext.Provider>
  );
}

InquiryContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// eslint-disable-next-line react/display-name
export const withInquiryContext = WrappedComponent => props => {
  return (
    <InquiryContextProvider>
      <WrappedComponent {...props} />
    </InquiryContextProvider>
  );
};
